/**
 * A failed passage, cut down to what an author needs to find it (ADR-0120).
 * Offsets are runes — code points — as the server counts them.
 */

/** How much of a long passage to show on each side of the words not said. */
const CONTEXT = 140;

/**
 * excerpt cuts a passage down to what locates it: the words not said with
 * some text either side. With no span there is nothing to locate, so the whole
 * passage is shown: cutting it could hide exactly the words that were skipped.
 * In runes (code points), as the server counts the span — a byte or UTF-16
 * offset would land inside a Persian letter or split a surrogate pair.
 */
export function excerpt(
  text: string,
  start?: number,
  end?: number,
): { before: string; unsaid: string; after: string } {
  const runes = Array.from(text);
  if (!hasSpan(runes.length, start, end)) return { before: text, unsaid: "", after: "" };
  const from = Math.max(0, start! - CONTEXT);
  const to = Math.min(runes.length, end! + CONTEXT);
  return {
    before: (from > 0 ? "…" : "") + runes.slice(from, start).join(""),
    unsaid: runes.slice(start, end).join(""),
    after: runes.slice(end, to).join("") + (to < runes.length ? "…" : ""),
  };
}

function hasSpan(length: number, start?: number, end?: number): boolean {
  return start !== undefined && end !== undefined && start >= 0 && end <= length && start < end;
}

/**
 * passageLine is where Show in text opens the editor, and whether to select
 * it. The words not said are selected when they are known. A passage that
 * failed as a whole opens on its first sentence, selected: all of it is
 * missing, so that sentence is where the missing audio starts. A passage with
 * skipped words that are not known opens on its first sentence **unselected**:
 * selecting it would point at words the narrator may well have said.
 */
export function passageLine(
  text: string,
  failure?: string,
  start?: number,
  end?: number,
): { line: string; select: boolean } {
  const runes = Array.from(text);
  if (hasSpan(runes.length, start, end)) {
    // One line of it: the editor finds text within a paragraph.
    const span = runes.slice(start, end).join("");
    return { line: (span.split("\n").find((l) => l.trim()) ?? span).trim(), select: true };
  }
  const line = text.trim().split("\n")[0] ?? "";
  const first = (line.split(/(?<=[.!?؟…])\s/)[0] ?? "").trim();
  return { line: first, select: failure !== "unsaid_run" };
}
